import React, { useEffect, useState } from "react";
import { useHistory } from "react-router-dom";

// QueryString
import queryString from "query-string";

// Components
import Button from "components/Common/Button/Button";
import AdHocHeader from "components/Screens/BQMQ/Voting/AdHocHeader/AdHocHeader";
import BQMQComments from "components/Screens/BQMQ/Voting/BQMQComments/BQMQComments";
import VotingRow from "components/Screens/BQMQ/Voting/VotingRow/VotingRow";

// Utils
import {
    getUserAnswerHistory,
    COMMENTS_NAME_MAP,
    getSessionUserComments,
    getQuestionsForSession,
    getResults,
} from "utils/bqmq.utils";
import { applyTimeZoneOffset } from "utils/common.utils";
import * as Database from "utils/database.utils";

// Context
import { useAuth } from "context/Auth.context";
import { useBQMQ } from "context/BQMQ.context";
import { useSettings } from "context/Settings.context";

// Styles
import BlankVotingPageContainer from "./BlankVotingPage.styles";

// Logger
import Logger from "utils/logger.utils";
import LoadingSpinner from "../../../components/Common/LoadingSpinner/LoadingSpinner";

const Log = new Logger();
Log.init("BQMQ: Session");

interface Question {
    id: number;
    text: string;
    label: string | null;
    order: number | null;
    choices: { text: string; id: number }[];
    factor: { text: string };
}

export default function BlankVotingPage() {
    const history = useHistory();
    const { user } = useAuth();
    const { setToast, setShowLoadingToast } = useSettings();
    const {
        myVotes,
        setMyVotes,
        getDropDowns,
        sessions,
        allFactorsList: allFactors,
        setDefaultChoices,
    } = useBQMQ();
    const [sendingAnswers, setSendingAnswers] = useState(false);
    const [dates, setDates] = useState<any>(null);
    const [sessionQuestions, setSessionQuestions] = useState<any[]>([]);
    const companyName = queryString.parse(window.location.search).company_name;
    const companyID = queryString.parse(window.location.search).company_id;
    const sessionID = queryString.parse(window.location.search).session_id;
    const date = queryString.parse(window.location.search).date;
    const isEditing = queryString.parse(window.location.search, {
        parseBooleans: true,
    }).editing;
    const [isLoading, setIsLoading] = useState(true);
    const activeQuestionList = sessionQuestions;

    const [initialComments, setInitialComments] = useState<
        Record<string, SessionComment[]>
    >({});
    const [comments, setComments] = useState<Record<string, SessionComment[]>>(
        {}
    );

    const currentSession = sessions.find(
        (session: any) => session.id === sessionID
    );
    const allFactorsList = Object.values(
        allFactors as BQMQContextFactor[]
    ).sort((a: any, b: any) => a.order - b.order);
    const currentFactor = allFactorsList.find(
        (factor: any) => factor.id === currentSession?.factor_id
    );

    const [sessionCoreResults, setSessionCoreResults] = useState<any>(null);
    const [sessionAllResults, setSessionAllResults] = useState<any>(null);
    const [isFormValid, setIsFormValid] = useState<boolean>(false);
    const [sectionCompleteStatus, setSectionCompleteStatus] = useState<{
        [key: string]: boolean;
    }>({
        BQ: false,
        MQ: false,
        FADE: false,
    });
    const [hasOpenCommentForms, setHasOpenCommentForms] = useState(false);

    const sectionsToComplete = Object.entries(sectionCompleteStatus).map(
        ([key, value]) => key
    );

    const handleCommentChange = (
        factorId: string,
        comments: SessionComment[]
    ) => {
        setComments((prev) => ({
            ...prev,
            [factorId]: comments,
        }));
    };

    const updateSectionCompletionStatus = (section: string) => {
        const questionsInSection = activeQuestionList.filter(
            (question: Question) =>
                question.factor.text === section &&
                !question.text.includes("comment")
        );

        const isCompleted = questionsInSection.every((question: Question) => {
            return (
                myVotes[question.id] !== undefined &&
                myVotes[question.id] !== null
            );
        });

        setSectionCompleteStatus((prevState) => ({
            ...prevState,
            [section]: isCompleted,
        }));

        validateForm();
    };

    const validateForm = () => {
        const hasIncompleteSection = Object.keys(sectionCompleteStatus).some(
            (section) => {
                const questionsInSection = activeQuestionList.filter(
                    (question: Question) =>
                        question.factor.text === section &&
                        !question.text.includes("comment")
                );

                const isStarted = questionsInSection.some(
                    (question: Question) =>
                        myVotes[question.id] !== undefined &&
                        myVotes[question.id] !== null
                );
                const isCompleted = questionsInSection.every(
                    (question: Question) =>
                        myVotes[question.id] !== undefined &&
                        myVotes[question.id] !== null
                );

                return isStarted && !isCompleted;
            }
        );

        setIsFormValid(!hasIncompleteSection);
    };

    const prepareVotePayload = () => {
        const votes = [];
        for (const [key, value] of Object.entries(myVotes)) {
            if (value) {
                const question = activeQuestionList.find(
                    (question: any) => question.id === key
                );
                if (!question) continue;
                let newValue = (value as string).toString().toLowerCase();
                if (newValue.length > 1) {
                    newValue =
                        newValue.charAt(0).toUpperCase() + newValue.slice(1);
                }
                const commentChoice = question.choices.find(
                    (choice: any) => choice.text === "Comment"
                );
                const choice = question.choices.find(
                    (choice: any) => choice.text === newValue
                );
                const vote = {
                    analyst_id: user.id,
                    question_id: key,
                    choice_id: choice ? choice.id : commentChoice.id,
                    session_id: sessionID,
                    comment: choice?.text === "Comment" ? "" : value,
                };
                votes.push(vote);
            }
        }
        return votes;
    };

    const prepareCommentPayload = () => {
        const commentsPayload = [];
        for (const [factorId, factorComments] of Object.entries(comments)) {
            if (currentFactor && currentFactor.id !== factorId) {
                continue;
            }

            if (factorComments && Array.isArray(factorComments)) {
                for (const commentObj of factorComments) {
                    if (
                        commentObj &&
                        commentObj.comment_text &&
                        commentObj.comment_text.trim()
                    ) {
                        commentsPayload.push({
                            ...commentObj,
                            // Factor has more unnecessary details, so use only what's needed for submitting comments
                            factor: {
                                id: commentObj.factor.id,
                                text: commentObj.factor.text,
                            },
                        });
                    }
                }
            }
        }
        return commentsPayload;
    };

    const saveData = async () => {
        if (hasOpenCommentForms) {
            setToast({
                show: true,
                type: "error",
                message:
                    "Please finish adding or editing your comments before saving",
            });
            return;
        }

        setSendingAnswers(true);
        setShowLoadingToast({ show: true, message: "SAVING ANSWERS" });

        const votes = prepareVotePayload();
        const commentsPayload = prepareCommentPayload();

        const payload = {
            votes,
            comments: commentsPayload,
        };

        const res = await Database.post(
            "api/bqmq_voting/submit-voting-form",
            payload
        );

        if (res.ok) {
            history.replace(
                `result/company?company_id=${companyID}&session_id=${sessionID}&company_name=${companyName}&date=${date}`
            );
            setMyVotes({});
            setToast({
                show: true,
                type: "success",
                message: "Your answers have been saved",
            });
            localStorage.removeItem(`myVotes ${user.id} ${sessionID}`);
            getDropDowns();
        } else {
            setSendingAnswers(false);
            setToast({
                show: true,
                type: "error",
                message: "There was an error saving your answers",
            });
        }
        setShowLoadingToast({ show: false, message: "SAVING ANSWERS" });
    };

    useEffect(() => {
        if (!date && currentSession?.date && sessionQuestions.length === 0) {
            const dateOnly = (currentSession.date as string).split("T")[0];
            getQuestionsForSession(dateOnly).then(setSessionQuestions);
        }
    }, [currentSession?.date]);

    useEffect(() => {
        const fetchData = async () => {
            setIsLoading(true);
            setShowLoadingToast({ show: true, message: "Loading" });
            if (date) {
                const sessionDate = Array.isArray(date) ? date[0] : date;
                const dateOnly = sessionDate.split("T")[0];
                const questions = await getQuestionsForSession(dateOnly);
                setSessionQuestions(questions);
            }
            await getUserAnswerHistory(
                user.id,
                sessionID,
                setDates,
                setMyVotes
            );
            await getSessionUserComments(
                user.id,
                sessionID,
                setInitialComments,
                setComments
            );
            setShowLoadingToast({ show: false, message: "Loading" });
            setIsLoading(false);
        };

        fetchData();
    }, []);

    // Remove cached unsaved votes for factors that are not currently editable
    useEffect(() => {
        const currentFactorShort = (currentFactor as any)?.short_name;
        if (!currentFactorShort) return; // Full vote: keep all cached values
        if (!activeQuestionList || activeQuestionList.length === 0) return;

        const allowedQuestionIds = new Set(
            activeQuestionList
                .filter((q: any) => q.factor?.text === currentFactorShort)
                .map((q: any) => String(q.id))
        );

        let changed = false;
        const filtered: Record<string, any> = {};
        for (const [qid, val] of Object.entries(myVotes || {})) {
            if (allowedQuestionIds.has(String(qid))) {
                filtered[qid] = val;
            } else {
                changed = true;
            }
        }
        if (changed) {
            setMyVotes(filtered);
        }
    }, [currentFactor, activeQuestionList, myVotes, setMyVotes]);

    useEffect(() => {
        getResults(
            companyID,
            sessionID,
            setDefaultChoices,
            setSessionAllResults,
            setSessionCoreResults
        );
    }, []);

    useEffect(() => {
        validateForm();
    }, [myVotes, activeQuestionList]);

    useEffect(() => {
        sectionsToComplete.forEach((section) => {
            updateSectionCompletionStatus(section);
        });
    }, [myVotes]);

    const SectionHeader = ({
        title,
        dates,
    }: {
        title: string;
        dates: string;
    }) => (
        <h3 className="sectionTitle">
            {dates &&
                `${title.toUpperCase()} - Last updated: ${applyTimeZoneOffset(
                    dates
                )}`}
        </h3>
    );

    const renderVotingRow = (question: Question, isEditable: boolean) => {
        const sectionName = question.factor.text;

        if (
            question.choices
                .map((obj: any) => obj.text)
                .some((value: string) =>
                    ["1", "2", "3", "4", "5"].includes(value)
                )
        ) {
            return (
                <VotingRow
                    questionID={question.id.toString()}
                    question={question.label ?? question.text}
                    disabled={sendingAnswers || !isEditable}
                    updateSectionCompletionStatus={() =>
                        updateSectionCompletionStatus(sectionName)
                    }
                />
            );
        }
        return null;
    };

    const renderAgreeDisagreeRow = (
        question: Question,
        isEditable: boolean
    ) => {
        const sectionName = question.factor.text;

        if (
            question.choices
                .map((obj: any) => obj.text)
                .some((value: string) =>
                    ["Agree", "Disagree", "Neutral"].includes(value)
                )
        ) {
            return (
                <VotingRow
                    questionID={question.id.toString()}
                    question={question.label ?? question.text}
                    agree
                    disabled={sendingAnswers || !isEditable}
                    updateSectionCompletionStatus={() =>
                        updateSectionCompletionStatus(sectionName)
                    }
                />
            );
        }
        return null;
    };

    const ActionRow = ({ children }: { children: React.ReactNode }) => (
        <div className="actionRow">{children}</div>
    );

    const isEverySectionUncompleted = Object.values(
        sectionCompleteStatus
    ).every((status) => status === false);

    if (isLoading) {
        return (
            <BlankVotingPageContainer className="pageContaine">
                <div className="loader">
                    <LoadingSpinner></LoadingSpinner>
                </div>
            </BlankVotingPageContainer>
        );
    }

    return (
        <BlankVotingPageContainer className="pageContainer">
            <AdHocHeader
                isEditing={isEditing}
                session={{ company_name: companyName }}
            />

            {allFactorsList.map((factor, index: number) => {
                const currentFactorShort = (currentFactor as any)?.short_name;
                const isEditable =
                    !currentFactorShort ||
                    factor.short_name === currentFactorShort;
                const filteredQuestions = activeQuestionList
                    ?.filter(
                        (question: Question) =>
                            question.factor.text === factor.short_name
                    )
                    ?.sort(
                        (a: Question, b: Question) =>
                            (a.order ?? 99) - (b.order ?? 99)
                    );

                return (
                    <React.Fragment key={`${factor.name}_${index}`}>
                        <SectionHeader title={factor.name} dates={dates} />

                        {filteredQuestions.map(
                            (question: Question, index: number) => (
                                <React.Fragment key={`${question.id}_${index}`}>
                                    {renderVotingRow(question, isEditable)}
                                    {renderAgreeDisagreeRow(
                                        question,
                                        isEditable
                                    )}
                                </React.Fragment>
                            )
                        )}
                        <BQMQComments
                            sessionId={sessionID}
                            factor={factor}
                            commentName={COMMENTS_NAME_MAP[factor.short_name]}
                            disabled={sendingAnswers || !isEditable}
                            initialComments={initialComments[factor.id] || []}
                            onCommentsChange={handleCommentChange}
                            onFormStateChange={setHasOpenCommentForms}
                        />
                        <hr />
                    </React.Fragment>
                );
            })}

            <ActionRow>
                <Button
                    text="SAVE"
                    click={saveData}
                    color="primary"
                    disabled={
                        !isFormValid ||
                        sendingAnswers ||
                        isEverySectionUncompleted
                    }
                />
            </ActionRow>
        </BlankVotingPageContainer>
    );
}
